import { GraphAgeType, GraphType, GrowthChartStandard, UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { IPatientGraphUI } from "@/Patient/Models/UI/Graph/IPatientGraphUI";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientGraphsHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalPatientOperations.Graphs.ToggleAllVitalsCheck, this.setAllVitalsCheck);
        this.RegisterOperation(LocalPatientOperations.Graphs.ToggleAllCurvesCheck, this.setAllCurvesCheck);
        this.RegisterOperation(LocalPatientOperations.Graphs.CurveItemCheck, this.curveItemCheck);

        this.RegisterOperation(LocalPatientOperations.Graphs.SetUnitSystem, this.setUnitSystem);
        this.RegisterOperation(LocalPatientOperations.Graphs.SetVersionChart, this.setVersionChart);

        this.RegisterOperation(LocalPatientOperations.Graphs.SetChartUIParam, this.setChartUIParam);

        this.RegisterOperation(LocalPatientOperations.MedicalNote.PrintCharts, this.printCharts);
    }

    private setChartUIParam(request: LocalEvent, store: PatientStore): void {
        this.setGraphUI(request, store, request.Get<string>("key"), request.Get<boolean>("value"));
    }

    private setAllVitalsCheck(request: LocalEvent, store: PatientStore): void {
        var checked = request.Get<boolean>("checked");

        this.mutateGraphUI(request, store, uim => {
            uim = uim.set("isHeadCircumferenceChecked", checked);
            uim = uim.set("isWeightChecked", checked);
            uim = uim.set("isHeightChecked", checked);
            uim = uim.set("isBmiChecked", checked);
            uim = uim.set("isTensionSittingChecked", checked);
            return uim;
        });
    }

    // GrowthCurves
    private curveItemCheck(request: LocalEvent, store: PatientStore): void {
        let checked = request.Get<boolean>("checked");
        let graphType = request.Get<GraphType>("graphType");
        let curveAgeType = request.Get<GraphAgeType>("curveAgeType");

        this.mutateGraphUI(request, store, uim => uim.set(
            "growthCurves",
            uim
                .get("growthCurves")
                .toArray()
                .map(x => {
                    if (x.type == graphType && x.ageType === curveAgeType)
                        x.checked = checked;
                    return x;
                })
                .toImmutable()
        ));
    }

    private setAllCurvesCheck(request: LocalEvent, store: PatientStore): void {
        let checked = request.Get<boolean>("checked");

        this.mutateGraphUI(request, store, uim => uim.set(
            "growthCurves",
            uim
                .get("growthCurves")
                .toArray()
                .map(x => {
                    x.checked = checked;
                    return x;
                })
                .toImmutable()
        ));
    }

    private setUnitSystem(request: LocalEvent, store: PatientStore): void {
        this.setGraphUI(request, store, "unitSystem", request.Get<UnitSystem>("unitSystem"));
    }

    private setVersionChart(request: LocalEvent, store: PatientStore): void {
        this.setGraphUI(request, store, "growthChartStandard", request.Get<GrowthChartStandard>("versionChart"));
    }

    private printCharts(request: LocalEvent, store: PatientStore): void {
        let uim = store.PatientChartsUIStateModels[request.PatientId];

        let curvesToPrint = uim.get("growthCurves").filter(x => !!uim.get("allGrowthCurvesChecked") || !!x.checked);

        store.ActionsMedicalNote.generateChartsPDF(
            request.PatientId,
            uim.get("isHeadCircumferenceChecked"),
            uim.get("isWeightChecked"),
            uim.get("isHeightChecked"),
            uim.get("isBmiChecked"),
            curvesToPrint.some(x => x.type == GraphType.HeadCircumference),
            curvesToPrint.some(x => x.type == GraphType.WeightForAge && x.ageType == GraphAgeType.Baby),
            curvesToPrint.some(x => x.type == GraphType.WeightForAge && x.ageType == GraphAgeType.Child),
            curvesToPrint.some(x => x.type == GraphType.HeightForAge),
            curvesToPrint.some(x => x.type == GraphType.BMI && x.ageType == GraphAgeType.Baby),
            curvesToPrint.some(x => x.type == GraphType.BMI && x.ageType == GraphAgeType.Child),
            curvesToPrint.some(x => x.type == GraphType.LengthForAge),
            uim.get("unitSystem"),
            uim.get("growthChartStandard")
        );
    }

    private setGraphUI(request: LocalEvent, store: PatientStore, param: any, value: any): IPatientGraphUI {
        return this.mutateGraphUI(request, store, uim => uim.set(param, value));
    }

    private mutateGraphUI(request: LocalEvent, store: PatientStore, mutate: (graphUI: IPatientGraphUI) => IPatientGraphUI): IPatientGraphUI {
        let uim = store.PatientChartsUIStateModels[request.PatientId];
        if (uim != null)
            uim = mutate(uim);

        uim = uim.set("allGrowthCurvesChecked", this.isAllgrowthCurvesChecked(uim));
        uim = uim.set("allVitalsChecked", this.isAllVitalChecked(uim));

        store.PatientChartsUIStateModels[request.PatientId] = uim;
        return uim;
    }

    private isAllVitalChecked(uim: IPatientGraphUI): boolean {
        return uim.get("isHeadCircumferenceChecked") && uim.get("isWeightChecked") && uim.get("isHeightChecked") && uim.get("isBmiChecked");
    }

    private isAllgrowthCurvesChecked(uim: IPatientGraphUI): boolean {
        let isChecked = false;
        if (uim.get("growthCurves"))
            isChecked = uim.get("growthCurves").every(x => !!x.checked);
        return isChecked;
    }
}

export { PatientGraphsHandler };
